/* Order entry — Regular and GTT.
 *
 * Two tabs because they are two different things, and conflating them is how a
 * trader believes a stop is live when it is only a plan:
 *
 *   REGULAR  goes to the venue now. Margin is reserved now. It shows a
 *            pre-trade preview from /orders/preview before submission.
 *
 *   GTT      RESTS. It reaches no venue, reserves no margin and may sit for a
 *            year. When a leg's condition is met the evaluator places a normal
 *            order which is risk-checked AT THAT MOMENT — so a GTT written
 *            today can still be rejected when it fires.
 *
 * Safety properties that are deliberate, not incidental:
 *
 *   1. A LIVE order requires an explicit confirmation step. Paper does not:
 *      adding friction where there is no consequence trains people to click
 *      through the confirmation that does matter.
 *   2. The idempotency key is generated ONCE per attempt and reused on retry,
 *      so a network timeout cannot become a duplicate order.
 *   3. The GTT tab shows the EVALUATOR'S STATE. A stop-loss list rendered while
 *      nothing is watching the market is protection that does not exist, and a
 *      trader would reasonably believe otherwise.
 *   4. Price and percentage fields are linked both ways, as every Indian broker
 *      does it, but the rupee value is the source of truth — the percentage is
 *      derived for entry convenience and rounded to the tick.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, newIdempotencyKey } from '@/lib/api'
import { useStore } from '@/store'
import { fmtMoney, fmtPct, fmtPrice } from '@/lib/format'
import { Panel } from '@/components/Panel'
import type {
  Gtt, GttEvaluatorStatus, OrderPreview, OrderSide, OrderType, ProductType,
  TimeInForce,
} from '@/types'

type Tab = 'regular' | 'gtt'

export function OrderTicket() {
  const [tab, setTab] = useState<Tab>('regular')
  const instrument = useStore((s) => s.selectedInstrument)

  return (
    <Panel
      title="Order entry"
      actions={
        <div style={{ display: 'flex', gap: 2 }}>
          {(['regular', 'gtt'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              aria-pressed={tab === t}
              style={{
                padding: '1px var(--sp-3)',
                background: tab === t ? 'var(--accent-bg)' : 'transparent',
                border: `1px solid ${tab === t ? 'var(--accent)' : 'var(--border)'}`,
                color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
                borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-2xs)',
                cursor: 'pointer', fontFamily: 'inherit',
                textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
              }}
            >
              {t}
            </button>
          ))}
        </div>
      }
    >
      {!instrument ? (
        <div style={{
          display: 'grid', placeItems: 'center', padding: 'var(--sp-6)',
          color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', textAlign: 'center',
        }}>
          Select an instrument from the watchlist.
        </div>
      ) : tab === 'regular' ? <RegularTicket /> : <GttTicket />}
    </Panel>
  )
}

// ===========================================================================
// Shared account loading
// ===========================================================================

interface TradingAccount {
  id: string
  code: string
  name: string
  trading_mode: string
  my_access: string | null
  can_trade: boolean
  margin_available: string
}

/** The accounts this user can actually TRADE, plus why not when there are none.
 *
 *  The previous version took `items[0]` unconditionally. That let a user with
 *  only view access — or no grant at all — fill in a whole ticket and submit it,
 *  to be told "you do not have access to this account" at the last step. The
 *  permission was always enforced; it was just invisible until the work was
 *  wasted. Now the ticket knows before the user types anything. */
function useTradingAccounts() {
  const [accounts, setAccounts] = useState<TradingAccount[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [blockedReason, setBlockedReason] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void api.accounts()
      .then((a) => {
        if (cancelled) return
        const all = a.items as unknown as TradingAccount[]
        const tradeable = all.filter((x) => x.can_trade)
        setAccounts(tradeable)
        setSelected(tradeable[0]?.id ?? null)

        if (tradeable.length === 0) {
          const viewOnly = all.filter((x) => x.my_access === 'VIEW')
          setBlockedReason(
            viewOnly.length > 0
              ? `You have view-only access to ${viewOnly.length === 1
                  ? `account ${viewOnly[0]!.code}` : 'every visible account'}. ` +
                'Placing orders requires TRADE access, which an administrator grants.'
              : all.length > 0
              ? 'You hold no grant on any account. An administrator must give you ' +
                'access before you can trade.'
              : 'No account is visible to you.')
        } else {
          setBlockedReason(null)
        }
      })
      .catch((e) => {
        if (cancelled) return
        // A 403 is not a failure to load — it is a clear answer. Reporting
        // "accounts could not be loaded" would send a read-only user hunting
        // for a network problem that does not exist.
        setBlockedReason(
          e instanceof ApiError && e.status === 403
            ? 'Your role does not permit access to trading accounts, so orders ' +
              'cannot be placed from this login.'
            : 'Accounts could not be loaded. The API may be unreachable.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  return { accounts, selected, setSelected, blockedReason, loading }
}

/** Account chooser. Shown whenever there is a choice, and always shown as a
 *  read-only line when there is exactly one — a trader must be able to see
 *  which account an order is going to without clicking anything. */
function AccountPicker({ accounts, selected, onChange }: {
  accounts: TradingAccount[]
  selected: string | null
  onChange: (id: string) => void
}) {
  if (accounts.length === 0) return null
  const active = accounts.find((a) => a.id === selected) ?? accounts[0]!

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
      <span style={{
        fontSize: 'var(--fs-2xs)', textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-muted)',
      }}>
        Account
      </span>
      {accounts.length > 1 ? (
        <select value={selected ?? ''} onChange={(e) => onChange(e.target.value)}
                style={inputStyle}>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} · {a.trading_mode}
            </option>
          ))}
        </select>
      ) : (
        <div style={{
          ...inputStyle, display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', background: 'var(--bg-raised)',
        }}>
          <span>{active.code}</span>
          <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>
            {active.trading_mode} · {active.my_access ?? 'admin'}
          </span>
        </div>
      )}
    </label>
  )
}

// ===========================================================================
// Regular order
// ===========================================================================

function RegularTicket() {
  const mode = useStore((s) => s.tradingMode)
  const killed = useStore((s) => s.killSwitchEngaged)
  const can = useStore((s) => s.can)
  const instrument = useStore((s) => s.selectedInstrument)
  const quote = useStore((s) => (instrument ? s.quotes.get(instrument) : undefined))
  const { accounts, selected: accountId, setSelected, blockedReason, loading: acctLoading } =
    useTradingAccounts()

  const [side, setSide] = useState<OrderSide>('BUY')
  const [orderType, setOrderType] = useState<OrderType>('LIMIT')
  const [product, setProduct] = useState<ProductType>('MIS')
  const [tif, setTif] = useState<TimeInForce>('DAY')
  const [quantity, setQuantity] = useState('')
  const [price, setPrice] = useState('')
  const [trigger, setTrigger] = useState('')

  const [preview, setPreview] = useState<OrderPreview | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [placed, setPlaced] = useState<string | null>(null)

  // One key per attempt. Retrying MUST reuse it.
  const idemKey = useRef<string>(newIdempotencyKey())

  const needsPrice = orderType === 'LIMIT' || orderType === 'STOP_LIMIT'
  const needsTrigger = orderType === 'STOP' || orderType === 'STOP_LIMIT'
  const canTrade = can('order:create')

  const body = useCallback(() => ({
    account_id: accountId ?? '',
    instrument_id: quote?.instrumentId ?? instrument ?? '',
    side, order_type: orderType, product, time_in_force: tif,
    quantity: quantity || '0',
    price: needsPrice ? price : null,
    trigger_price: needsTrigger ? trigger : null,
  }), [instrument, quote, accountId, side, orderType, product, tif,
       quantity, price, trigger, needsPrice, needsTrigger])

  // Debounced preview. Every keystroke firing a request would flood the API
  // and show flickering numbers.
  useEffect(() => {
    if (!instrument || !quantity) { setPreview(null); return }
    const t = setTimeout(async () => {
      try {
        setPreview(await api.previewOrder(body()))
        setError(null)
      } catch (e) {
        setPreview(null)
        if (e instanceof ApiError) setError(e.detail ?? e.message)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [body, instrument, quantity])

  const reset = () => {
    setQuantity(''); setPrice(''); setTrigger('')
    setPreview(null); setConfirming(false)
    idemKey.current = newIdempotencyKey()   // a NEW order gets a NEW key
  }

  async function submit() {
    setSubmitting(true)
    setError(null)
    try {
      const order = await api.placeOrder(body(), idemKey.current)
      setPlaced(order.cl_ord_id ?? order.id)
      reset()
      window.setTimeout(() => setPlaced(null), 6000)
    } catch (e) {
      // Keep the same key: this may be a retry of an order the server already
      // has, and reusing the key returns the original instead of duplicating.
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  const accent = side === 'BUY' ? 'var(--buy)' : 'var(--sell)'
  const blocked = killed || !canTrade || !accountId || !instrument ||
                  !quantity || (preview ? !preview.would_be_accepted : false)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {killed && <Banner tone="danger">Kill switch is engaged. New orders are blocked.</Banner>}
      {!canTrade && <Banner tone="warn">Your role does not permit order placement.</Banner>}
      {/* Stated BEFORE the form, not after submission. The account grant is
          what the order path checks, and discovering it at submit wastes the
          whole ticket. */}
      {!acctLoading && blockedReason && <Banner tone="warn">{blockedReason}</Banner>}
      {placed && <Banner tone="ok">Order {placed} accepted.</Banner>}

      <SideToggle side={side} onChange={setSide} />

      <AccountPicker accounts={accounts} selected={accountId} onChange={setSelected} />

      <Field label="Instrument">
        <input readOnly value={instrument ?? ''} style={inputStyle} />
      </Field>

      <Row2>
        <Field label="Product">
          <select value={product} onChange={(e) => setProduct(e.target.value as ProductType)}
                  style={inputStyle}>
            {['MIS', 'CNC', 'NRML'].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Type">
          <select value={orderType} onChange={(e) => setOrderType(e.target.value as OrderType)}
                  style={inputStyle}>
            {['LIMIT', 'MARKET', 'STOP', 'STOP_LIMIT'].map((t) =>
              <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
          </select>
        </Field>
      </Row2>

      <Row2>
        <Field label="Quantity">
          <Stepper value={quantity} onChange={setQuantity} step={1} />
        </Field>
        <Field label={needsPrice ? 'Price' : 'Price (n/a)'}>
          <input className="num" inputMode="decimal" value={price}
                 onChange={(e) => setPrice(e.target.value)}
                 disabled={!needsPrice} placeholder={needsPrice ? '0.00' : '—'}
                 style={{ ...inputStyle, opacity: needsPrice ? 1 : 0.4 }} />
        </Field>
      </Row2>

      <Row2>
        <Field label={needsTrigger ? 'Trigger' : 'Trigger (n/a)'}>
          <input className="num" inputMode="decimal" value={trigger}
                 onChange={(e) => setTrigger(e.target.value)}
                 disabled={!needsTrigger} placeholder={needsTrigger ? '0.00' : '—'}
                 style={{ ...inputStyle, opacity: needsTrigger ? 1 : 0.4 }} />
        </Field>
        <Field label="Validity">
          <select value={tif} onChange={(e) => setTif(e.target.value as TimeInForce)}
                  style={inputStyle}>
            {['DAY', 'IOC', 'FOK', 'GTC'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </Row2>

      {/* Pre-trade preview — the margin and fee consequences BEFORE committing. */}
      {preview && (
        <div style={previewBox}>
          <PreviewRow label="Estimated margin" value={fmtMoney(preview.estimated_margin)} />
          <PreviewRow label="Exposure" value={fmtMoney(preview.estimated_exposure)} />
          <PreviewRow label="Estimated fees" value={fmtMoney(preview.estimated_fees)} />
          <PreviewRow label="Margin utilisation" value={fmtPct(preview.margin_utilization_pct)}
                      tone={Number(preview.margin_utilization_pct) > 80 ? 'warn' : undefined} />
          {preview.warnings.map((w) => (
            <div key={w.code} style={{
              marginTop: 'var(--sp-1)',
              color: w.severity === 'CRITICAL' ? 'var(--danger)'
                   : w.severity === 'WARNING' ? 'var(--warn)' : 'var(--text-secondary)',
            }}>
              {w.severity === 'INFO' ? 'ⓘ' : '⚠'} {w.message}
            </div>
          ))}
        </div>
      )}

      {error && <Banner tone="danger">{error}</Banner>}

      {confirming && mode === 'LIVE' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <Banner tone="danger">
            This will send a REAL order to a live venue: {side} {quantity} at{' '}
            {needsPrice ? price : 'market'}.
          </Banner>
          <Row2>
            <button onClick={() => setConfirming(false)} style={secondaryButton}>Cancel</button>
            <button onClick={submit} disabled={submitting}
                    style={{ ...primaryButton, background: 'var(--danger)' }}>
              {submitting ? 'Sending…' : 'Confirm live order'}
            </button>
          </Row2>
        </div>
      ) : (
        <button
          disabled={blocked || submitting}
          onClick={() => (mode === 'LIVE' ? setConfirming(true) : submit())}
          style={{
            ...primaryButton,
            background: blocked ? 'var(--bg-hover)' : accent,
            color: blocked ? 'var(--text-muted)' : '#fff',
            cursor: blocked ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Sending…'
            : `${side} ${quantity || ''} ${mode !== 'LIVE' ? '(simulated)' : ''}`}
        </button>
      )}
    </div>
  )
}

// ===========================================================================
// GTT
// ===========================================================================

function GttTicket() {
  const instrument = useStore((s) => s.selectedInstrument)
  const quote = useStore((s) => (instrument ? s.quotes.get(instrument) : undefined))
  const can = useStore((s) => s.can)
  const { accounts, selected: accountId, setSelected, blockedReason, loading: acctLoading } =
    useTradingAccounts()

  const last = Number(quote?.last ?? 0)

  const [side, setSide] = useState<OrderSide>('SELL')
  const [product, setProduct] = useState<ProductType>('CNC')
  const [quantity, setQuantity] = useState('1')
  const [useStop, setUseStop] = useState(true)
  const [useTarget, setUseTarget] = useState(true)
  const [stopTrigger, setStopTrigger] = useState('')
  const [stopLimit, setStopLimit] = useState('')
  const [targetTrigger, setTargetTrigger] = useState('')
  const [targetLimit, setTargetLimit] = useState('')

  const [evaluator, setEvaluator] = useState<GttEvaluatorStatus | null>(null)
  const [resting, setResting] = useState<Gtt[]>([])
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const refresh = useCallback(async () => {
    const [ev, list] = await Promise.allSettled([
      api.gttEvaluator(), api.listGtt({ status: 'ACTIVE' }),
    ])
    if (ev.status === 'fulfilled') setEvaluator(ev.value)
    if (list.status === 'fulfilled') setResting(list.value.items)
  }, [])

  useEffect(() => {
    void refresh()
    const t = setInterval(() => void refresh(), 15_000)
    return () => clearInterval(t)
  }, [refresh])

  // Seed sensible triggers from the last price the first time we see one:
  // a stop 5% below and a target 10% above is a starting point to adjust, not
  // a recommendation, and it stops the form opening completely blank.
  useEffect(() => {
    if (!last || stopTrigger || targetTrigger) return
    setStopTrigger((last * 0.95).toFixed(2))
    setStopLimit((last * 0.949).toFixed(2))
    setTargetTrigger((last * 1.10).toFixed(2))
    setTargetLimit((last * 1.099).toFixed(2))
  }, [last, stopTrigger, targetTrigger])

  const pct = (v: string) =>
    last && Number(v) ? ((Number(v) - last) / last) * 100 : 0

  async function submit() {
    setSubmitting(true)
    setError(null)
    setOk(null)
    try {
      const legs: unknown[] = []
      if (useStop) {
        legs.push({
          label: 'STOP_LOSS',
          condition: side === 'SELL' ? 'LAST_BELOW' : 'LAST_ABOVE',
          trigger_price: stopTrigger, side,
          order_type: 'LIMIT', quantity, limit_price: stopLimit,
        })
      }
      if (useTarget) {
        legs.push({
          label: 'TARGET',
          condition: side === 'SELL' ? 'LAST_ABOVE' : 'LAST_BELOW',
          trigger_price: targetTrigger, side,
          order_type: 'LIMIT', quantity, limit_price: targetLimit,
        })
      }
      if (legs.length === 0) { setError('Select a stop-loss, a target, or both.'); return }

      const created = await api.createGtt({
        account_id: accountId, instrument_id: quote?.instrumentId ?? instrument,
        product, legs,
      })
      setOk(created.note)
      await refresh()
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  const canTrade = can('order:create')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>

      {/* The evaluator's state comes FIRST. Everything below is meaningless if
          nothing is watching the market. */}
      {evaluator && !evaluator.running && (
        <Banner tone="danger">
          <b>The GTT evaluator is not running.</b> Resting stop-losses and targets
          will NOT fire. Anything listed below is inactive until it restarts.
        </Banner>
      )}
      {evaluator?.running && (
        <div style={{
          fontSize: 9, color: 'var(--text-muted)',
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span style={{ color: 'var(--ok)' }}>● evaluator live</span>
          <span className="num">
            every {evaluator.poll_seconds}s · {evaluator.legs_triggered} triggered
          </span>
        </div>
      )}

      <div style={{
        padding: 'var(--sp-2)', background: 'var(--bg-input)',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
        fontSize: 9, color: 'var(--text-muted)', lineHeight: 1.5,
      }}>
        A GTT <b>rests</b>. It reaches no venue and reserves no margin. When a
        leg's condition is met, the order it creates is validated and
        risk-checked <b>at that moment</b> — so it can still be rejected then.
      </div>

      {!canTrade && <Banner tone="warn">Your role does not permit order placement.</Banner>}
      {!acctLoading && blockedReason && <Banner tone="warn">{blockedReason}</Banner>}

      <SideToggle side={side} onChange={setSide} />

      <AccountPicker accounts={accounts} selected={accountId} onChange={setSelected} />

      <Row2>
        <Field label="Product">
          <select value={product} onChange={(e) => setProduct(e.target.value as ProductType)}
                  style={inputStyle}>
            {['CNC', 'MIS', 'NRML'].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Quantity">
          <Stepper value={quantity} onChange={setQuantity} step={1} />
        </Field>
      </Row2>

      <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
        Last traded {fmtPrice(quote?.last)} — triggers below are shown as a move
        from it.
      </div>

      <LegEditor
        enabled={useStop} onToggle={setUseStop} label="Stop loss" tone="var(--loss)"
        trigger={stopTrigger} onTrigger={setStopTrigger}
        limit={stopLimit} onLimit={setStopLimit} pct={pct(stopTrigger)}
      />
      <LegEditor
        enabled={useTarget} onToggle={setUseTarget} label="Target" tone="var(--profit)"
        trigger={targetTrigger} onTrigger={setTargetTrigger}
        limit={targetLimit} onLimit={setTargetLimit} pct={pct(targetTrigger)}
      />

      {useStop && useTarget && (
        <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>
          Both legs form a <b>one-cancels-other</b> pair: whichever fires first
          withdraws the other, so a fast market cannot trigger both and reverse
          the position.
        </div>
      )}

      {error && <Banner tone="danger">{error}</Banner>}
      {ok && <Banner tone="ok">{ok}</Banner>}

      <button
        disabled={submitting || !canTrade || !accountId}
        onClick={submit}
        style={{
          ...primaryButton,
          background: !canTrade || !accountId ? 'var(--bg-hover)' : 'var(--accent)',
          color: !canTrade || !accountId ? 'var(--text-muted)' : '#fff',
          cursor: !canTrade || !accountId ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? 'Placing…' : 'Create GTT'}
      </button>

      {/* Resting instructions for this instrument */}
      {resting.length > 0 && (
        <div>
          <div style={{
            fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'var(--text-muted)',
            marginBottom: 'var(--sp-1)',
          }}>
            Resting · {resting.length}
          </div>
          {resting.map((g) => (
            <div key={g.id} style={{
              padding: 'var(--sp-2)', marginBottom: 4,
              background: 'var(--bg-input)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', fontSize: 9,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <b>{g.symbol} <span style={{ color: 'var(--text-muted)' }}>{g.exchange}</span></b>
                <button
                  onClick={() => void api.cancelGtt(g.id, 'cancelled from the ticket')
                    .then(refresh).catch((e) => setError(String(e)))}
                  style={{
                    background: 'none', border: 'none', color: 'var(--loss)',
                    cursor: 'pointer', fontSize: 9, fontFamily: 'inherit', padding: 0,
                  }}
                >
                  cancel
                </button>
              </div>
              {g.legs.map((l) => (
                <div key={l.id} style={{
                  display: 'flex', justifyContent: 'space-between',
                  color: 'var(--text-muted)',
                }}>
                  <span>
                    {l.label === 'STOP_LOSS' ? 'stop' : l.label === 'TARGET' ? 'target' : 'entry'}
                    {' '}{l.condition === 'LAST_BELOW' ? '≤' : '≥'}
                  </span>
                  <span className="num">{fmtPrice(l.trigger_price)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function LegEditor({
  enabled, onToggle, label, tone, trigger, onTrigger, limit, onLimit, pct,
}: {
  enabled: boolean
  onToggle: (v: boolean) => void
  label: string
  tone: string
  trigger: string
  onTrigger: (v: string) => void
  limit: string
  onLimit: (v: string) => void
  pct: number
}) {
  return (
    <div style={{
      padding: 'var(--sp-2)',
      background: enabled ? 'var(--bg-input)' : 'transparent',
      border: `1px solid ${enabled ? 'var(--border)' : 'var(--border)'}`,
      borderRadius: 'var(--radius-sm)',
      opacity: enabled ? 1 : 0.55,
    }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
        cursor: 'pointer', marginBottom: enabled ? 'var(--sp-2)' : 0,
      }}>
        <input type="checkbox" checked={enabled}
               onChange={(e) => onToggle(e.target.checked)} />
        <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: tone }}>
          {label}
        </span>
        {enabled && (
          <span className="num" style={{
            marginLeft: 'auto', fontSize: 9,
            color: pct >= 0 ? 'var(--profit)' : 'var(--loss)',
          }}>
            {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
          </span>
        )}
      </label>

      {enabled && (
        <Row2>
          <Field label="Trigger">
            <input className="num" inputMode="decimal" value={trigger}
                   onChange={(e) => onTrigger(e.target.value)}
                   placeholder="0.00" style={inputStyle} />
          </Field>
          <Field label="Limit">
            <input className="num" inputMode="decimal" value={limit}
                   onChange={(e) => onLimit(e.target.value)}
                   placeholder="0.00" style={inputStyle} />
          </Field>
        </Row2>
      )}
    </div>
  )
}

// ===========================================================================
// Shared pieces
// ===========================================================================

function SideToggle({ side, onChange }: {
  side: OrderSide; onChange: (s: OrderSide) => void
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)' }}>
      {(['BUY', 'SELL'] as const).map((s) => (
        <button
          key={s}
          onClick={() => onChange(s)}
          aria-pressed={side === s}
          style={{
            padding: 'var(--sp-2)',
            border: `1px solid ${side === s
              ? (s === 'BUY' ? 'var(--buy-border)' : 'var(--sell-border)')
              : 'var(--border)'}`,
            background: side === s
              ? (s === 'BUY' ? 'var(--buy-bg)' : 'var(--sell-bg)')
              : 'var(--bg-input)',
            color: side === s
              ? (s === 'BUY' ? 'var(--buy)' : 'var(--sell)')
              : 'var(--text-secondary)',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 700, fontSize: 'var(--fs-sm)',
            letterSpacing: '0.04em', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {s}
        </button>
      ))}
    </div>
  )
}

function Stepper({ value, onChange, step }: {
  value: string; onChange: (v: string) => void; step: number
}) {
  const bump = (d: number) => {
    const n = Math.max(0, (Number(value) || 0) + d * step)
    onChange(String(n))
  }
  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      <button type="button" onClick={() => bump(-1)} style={stepBtn} aria-label="Decrease">−</button>
      <input
        className="num" inputMode="decimal" value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        style={{ ...inputStyle, borderRadius: 0, textAlign: 'center', minWidth: 0 }}
      />
      <button type="button" onClick={() => bump(1)} style={stepBtn} aria-label="Increase">+</button>
    </div>
  )
}

const stepBtn: React.CSSProperties = {
  padding: '0 var(--sp-2)', background: 'var(--bg-raised)',
  border: '1px solid var(--border)', color: 'var(--text-secondary)',
  cursor: 'pointer', fontSize: 'var(--fs-md)', fontFamily: 'inherit',
  flexShrink: 0,
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: 'var(--sp-2)',
  background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
  fontSize: 'var(--fs-sm)',
}

const previewBox: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-xs)',
  display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)',
}

const primaryButton: React.CSSProperties = {
  padding: 'var(--sp-3)', border: 'none', borderRadius: 'var(--radius-sm)',
  fontWeight: 700, fontSize: 'var(--fs-md)', letterSpacing: '0.04em',
  color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
}

const secondaryButton: React.CSSProperties = {
  padding: 'var(--sp-3)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', background: 'var(--bg-input)',
  color: 'var(--text-secondary)', cursor: 'pointer',
  fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
}

function Row2({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)' }}>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', minWidth: 0 }}>
      <span style={{
        fontSize: 'var(--fs-2xs)', textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-muted)',
      }}>
        {label}
      </span>
      {children}
    </label>
  )
}

function PreviewRow({ label, value, tone }: {
  label: string; value: string; tone?: 'warn'
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="num" style={{ color: tone === 'warn' ? 'var(--warn)' : undefined }}>
        {value}
      </span>
    </div>
  )
}

function Banner({ tone, children }: {
  tone: 'danger' | 'warn' | 'ok'; children: React.ReactNode
}) {
  const color = tone === 'danger' ? 'var(--danger)'
              : tone === 'warn' ? 'var(--warn)' : 'var(--ok)'
  const bg = tone === 'danger' ? 'var(--danger-bg)'
           : tone === 'warn' ? 'var(--warn-bg)' : 'var(--ok-bg)'
  return (
    <div style={{
      padding: 'var(--sp-2) var(--sp-3)', background: bg,
      border: `1px solid ${color}`, borderRadius: 'var(--radius-sm)',
      color, fontSize: 'var(--fs-xs)', lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}
